import React, { createContext, useMemo, useContext, useEffect, useState, useCallback } from 'react';

// makes a simple event bus so we can dispatch erks/smirks from anywhere in the app
var makeBus = function makeBus() {
  var listeners = {};

  var subscribe = function subscribe(key, callback) {
    if (!listeners[key]) {
      listeners[key] = [];
    }

    listeners[key].push(callback);

    var unsubscribe = function unsubscribe() {
      var i = listeners[key].indexOf(callback);

      if (i >= 0) {
        listeners[key].splice(i, 1);
      }

      if (listeners[key].length === 0) {
        delete listeners[key];
      }
    };

    return unsubscribe;
  };

  var dispatch = function dispatch(key, evt) {
    if (listeners[key]) {
      listeners[key].forEach(function (callback) {
        return callback(evt);
      });
    }
  };

  return {
    subscribe: subscribe,
    dispatch: dispatch,
    __sharedState__: {}
  };
};

var bus = makeBus();
var context = createContext(bus);
var Provider = function Provider(_ref) {
  var value = _ref.value,
      children = _ref.children;
  var bus = useMemo(function () {
    return value != null ? value : makeBus();
  }, [value]);
  return /*#__PURE__*/React.createElement(context.Provider, {
    value: bus
  }, children);
};

var useBus = function useBus() {
  return useContext(context);
};

var useDispatch = function useDispatch() {
  return useBus().dispatch;
};

var useSubscribe = function useSubscribe(key, callback) {
  var deps = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
  var bus = useBus();
  useEffect(function () {
    return bus.subscribe(key, callback);
  }, [key, bus.subscribe].concat(deps));
};

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var useCache = function useCache() {
  return useBus().__sharedState__;
};

var useSharedState = function useSharedState(key, initialData) {
  var cache = useCache();

  if (!Object.hasOwnProperty.call(cache, key)) {
    cache[key] = initialData;
  }

  var dispatch = useDispatch();

  var _useState = useState(cache[key]),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1]; // this setState function sets the local state and also notifies all other calls to this state


  var setSharedState = useCallback(function (value) {
    cache[key] = value;
    setState(value);
    dispatch("@ux/hooks/shared-state/".concat(key), value);
  }, [key, setState, dispatch]); // listen for other sharedState anyupdates
  // @ts-ignore

  useSubscribe("@ux/hooks/shared-state/".concat(key), function (value) {
    if (value !== state) {
      setState(value);
    }
  }, [key, state]);
  return [state, setSharedState];
};

export { Provider, bus, makeBus, useBus, useDispatch, useSharedState, useSubscribe };
